import { Box, CircularProgress, Typography } from '@material-ui/core'
import { InventoryPresenter } from 'components/domain/stockPricing'
import { useUser } from 'components/providers/UserProvider'
import { useConfirm, useDialog, useErrorHandler } from 'lib/hooks'
import { useFetchInventoryDiff } from 'lib/hooks/useFetchInventoryDiff'
import { useFetchUserPtInventory } from 'lib/hooks/useFetchUserPtInventory'
import { useInventoryStore } from 'lib/hooks/useInventoryStore'
import { usePersistentStock } from 'lib/hooks/usePersistentStock'
import { FetchedStockInterface, useStockStore } from 'lib/hooks/useStockStore'
import { isArticleEdited } from 'lib/utils'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { unstable_batchedUpdates } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Redirect } from 'react-router-dom'
import {
  InventoryArticle,
  InventoryFetchState,
  isInventoryStale,
  isSingle,
  PublishChangesResponse,
  StockTab,
  STOCK_TYPES,
  User,
} from 'shared'
import { STOCK_DISPLAY } from 'utils/constants'
import { shallow } from 'zustand/shallow'
import { PublishChangesDialog } from './PublishChangesDialog'
import { SaveChangesToInventoryDialog } from './SaveChangesToInventoryDialog'

export const useHandleSettingsChange = () => {
  const { handle } = useErrorHandler()
  const { setUser } = useUser()

  //change this to do naive update and then validate

  // Returns true on success, false if the request failed. handleChange
  // swallows the error (shows a snackbar + logs it), so callers that need to
  // gate follow-up actions — e.g. a success snackbar or closing a dialog —
  // must check the boolean instead of relying on the await not throwing.
  const handleChange = useCallback(
    async (apiRequest: () => Promise<Partial<User>>): Promise<boolean> => {
      try {
        const response = await apiRequest()
        setUser((user) => ({ ...user!, ...response }))
        return true
      } catch (err: any) {
        console.error('useHandleSettingsChange: settings change failed', err)
        handle(err)
        return false
      }
    },
    [setUser]
  )
  return handleChange
}

export const StockPricingPresenter = (): JSX.Element => {
  const { t } = useTranslation()
  const { activeGame, user, setUser } = useUser()
  const { handle } = useErrorHandler()
  const [zoomLevel] = useInventoryStore((s) => [s.zoomLevel], shallow)
  const [activeStockType, setRefreshedStock, setActiveStockType, setStockLock] = useStockStore(
    (s) => [s.activeStockType, s.setRefreshedStock, s.setActiveStockType, s.setStockLock],
    shallow
  )
  const [zustandStock, setZustandStock] = useStockStore((state) => [
    state.fetchedStock,
    state.setFetchedStock,
  ])

  /**
   * fetched stock is the stock that is saved to local storage. It is saved only when a fetch is performed from the database.
   * Therefore, it maintains a snapshot of what is actually saved.
   *
   * zustandStock is the data structure with which inventory presenter works. It stores work-in-progress changes to items.
   */

  const [fetchedStock, setFetchedStock, loading] = usePersistentStock({
    stock: [],
    sealedStock: [],
    fetchedAt: '',
    lastFetchedFromDb: '',
  })

  const [setFetchingDialogOpen, FetchingDialog, fetchingDialogOpen] = useDialog()
  const [setPublishErrorsDialogOpen, PublishErrorsDialog] = useDialog()
  const { fetchUserPtInventory } = useFetchUserPtInventory()
  const [showDialogForGradedCardsWithoutGrade, setShowDialogForGradedCardsWithoutGrade] =
    useState(false)

  const [fetchState, setFetchState] = useState(InventoryFetchState.IDLE)
  const [publishChangesDialogOpen, setPublishChangesDialogOpen] = useState(false)
  const [saveToInventoryDialogOpen, setSaveToInventoryDialogOpen] = useState(false)

  const { startPolling, fetchInventoryStatus } = useFetchInventoryDiff({
    onPollingError: async (err) => {
      console.error(err)
      await fetchStock()
    },
    fetchedStock,
    setFetchedStock,
  })

  useEffect(() => {
    if (loading) return
    // lock the stock while deciding what to do
    setStockLock(true)

    const shouldForceFullFetch = isInventoryStale(fetchedStock.lastFetchedFromDb)

    if (shouldForceFullFetch) {
      fetchStock()
    } else {
      safeStartPolling()
    }
    setFetchState(InventoryFetchState.IDLE)
    // setStockLock(false)
  }, [loading])

  const fetchStock = useCallback(async () => {
    setFetchState(InventoryFetchState.FETCHING)
    if (!fetchingDialogOpen) setFetchingDialogOpen(true)
    try {
      const { stock: allStockData, maxProcessedAt } = await fetchUserPtInventory(
        activeGame.idGame,
        zoomLevel
      )

      const stockData: any[] = []
      const sealedStockData: any[] = []
      allStockData.forEach((article) => {
        if (isSingle(article.card)) {
          stockData.push(article)
        } else {
          sealedStockData.push(article)
        }
      })

      const genericSealedStockData: any[] = []
      sealedStockData.push(...genericSealedStockData)

      const now = new Date().toISOString()
      const newState = {
        stock: stockData,
        sealedStock: sealedStockData,
        fetchedAt: maxProcessedAt ?? now,
        lastFetchedFromDb: now,
      }

      unstable_batchedUpdates(() => {
        setFetchState(InventoryFetchState.IDLE)
        setFetchedStock(newState as FetchedStockInterface) // insert data into persistent stock
        setZustandStock(newState) // I forcefully create a new reference so react updates
        setRefreshedStock(true)
        setStockLock(false)
      })

      if (stockData.some((article) => article.grade === 'Unknown'))
        setShowDialogForGradedCardsWithoutGrade(true)
    } catch (error: any) {
      handle(error)
      // release the stock lock on failure so the UI doesn't stay stuck
      setStockLock(false)
      if (error?.response?.data?.error?.message === 'missingCardmarketConnection') {
        setUser((user) => ({ ...user!, cardmarketUsername: '' }))
        setFetchState(InventoryFetchState.FAILED_DISCONNECTED_ACCOUNT)
        throw new Error('missingCardmarketConnection')
      } else {
        setFetchState(InventoryFetchState.FAILED_UNKNOWN)
      }
    }
    setFetchingDialogOpen(false)
  }, [activeGame.idGame])

  const safeStartPolling = useCallback(async () => {
    try {
      // first call: fetch actual inventory state
      const isProcessing = await fetchInventoryStatus(activeGame.idGame)

      // if there are pt transactions → start polling
      if (isProcessing) {
        startPolling(activeGame.idGame)
        return
      }

      // if there aren't pt transactions → all updated
    } catch (error) {
      // if inventory diff fail → fallback on node
      fetchStock()
    }
  }, [activeGame.idGame])

  const [confirmFetchStock, ConfirmFetchStockDialog] = useConfirm(
    t('stockPricing.areYouSureRefresh'),
    '',
    () => fetchStock()
  )

  const someArticleEditedRef = useRef(false)
  someArticleEditedRef.current = Boolean(zustandStock!.stock.find((a) => isArticleEdited(a)))

  /**
   * Now  onFetchStock does not change when change selections/updates but only if changes his dipendencies confirmFetchStock, fetchStock).
   */
  const onFetchStock = useCallback(async () => {
    if (someArticleEditedRef.current) {
      confirmFetchStock()
    } else {
      safeStartPolling()
    }
  }, [confirmFetchStock, fetchStock])

  const determineTab = useCallback((article: InventoryArticle): StockTab => {
    if (article.failedAutopricing) return 'failedAutopricing'
    else if (article.isEdited()) return 'edited'
    else return 'unedited'
  }, [])

  const refreshStockAction = useMemo(
    () => ({
      key: 'get-stock-btn',
      fn: onFetchStock,
      icon: 'fa-light fa-arrows-rotate-reverse',
      actionLabel: t('stockPricing.actions.refreshStock'),
      tooltip: (fetchedAt: string) => (
        <>
          <Typography variant="body2">
            {t('stockPricing.stockFetchedOn')} <b>{fetchedAt}</b>
          </Typography>
          <Typography variant="body2">
            <b>{t('stockPricing.clickToRefresh')}</b>
          </Typography>
        </>
      ),
    }),
    [onFetchStock]
  )

  const extraDockActions = useMemo(
    () => [
      {
        key: 'singles-stock-btn',
        icon: 'fa-light fa-image',
        actionLabel: t('stockPricing.actions.singlesStock'),
        fn: () => {
          setActiveStockType(STOCK_TYPES.SINGLES)
          setRefreshedStock(true)
        },
      },
      {
        key: 'sealed-stock-btn',
        icon: 'fa-light fa-dice',
        actionLabel: t('stockPricing.actions.sealedStock'),
        fn: () => {
          setActiveStockType(STOCK_TYPES.SEALED)
          setRefreshedStock(true)
        },
      },
    ],
    [setActiveStockType, setRefreshedStock, t]
  )

  const openSaveToInventoryDialog = useCallback(() => {
    setSaveToInventoryDialogOpen(true)
  }, [setSaveToInventoryDialogOpen])

  // Must stay after every hook: the subscription can lock mid-session (e.g. the user returned by a
  // publish), and an early return before a hook crashes the render instead of redirecting.
  if (user?.subscription.allFeaturesDisabled || user?.featureFlags?.banned) {
    return <Redirect to="/" />
  }

  const emptyStockText = {
    [InventoryFetchState.FETCHING]: t('stockPricing.loadingStock'),
    [InventoryFetchState.IDLE]: t('stockPricing.noStock', { game: activeGame.name }),
    [InventoryFetchState.FAILED_DISCONNECTED_ACCOUNT]: t('error.fetchStock'),
    [InventoryFetchState.FAILED_UNKNOWN]: t('error.fetchStockUnknown'),
  }[fetchState]

  const handleChangesPublished = (publishResponse: PublishChangesResponse) => {
    if (publishResponse && publishResponse.status !== 200) {
      setPublishErrorsDialogOpen(true)
    }
  }

  return (
    <div className="stock-pricing-presenter">
      <InventoryPresenter
        determineTab={determineTab}
        emptyStockText={emptyStockText}
        stockState={zustandStock}
        setStockState={setZustandStock}
        refreshStockAction={refreshStockAction}
        extraDockActions={extraDockActions}
        openPublishDialog={openSaveToInventoryDialog}
        parentPage={STOCK_DISPLAY}
        showDialogForGradedCardsWithoutGrade={showDialogForGradedCardsWithoutGrade}
        setShowDialogForGradedCardsWithoutGrade={setShowDialogForGradedCardsWithoutGrade}
      />
      <FetchingDialog
        title={t('stockPricing.loadingStock')}
        content={
          <>
            <Box marginTop={3} height="8rem" width="fit-content" margin="0 auto">
              <CircularProgress size={100} />
            </Box>
          </>
        }
      />
      <ConfirmFetchStockDialog />
      {/* save to PT inventory */}
      {saveToInventoryDialogOpen && (
        <SaveChangesToInventoryDialog
          articles={
            activeStockType === 'singles'
              ? zustandStock.stock.filter(
                  (item) => (item.isEdited() || item.hasEditedLocations()) && item.idArticle
                )
              : zustandStock.sealedStock.filter((item) => item.isEdited() && item.idArticle)
          }
          handleChangesPublished={handleChangesPublished}
          open={saveToInventoryDialogOpen}
          setOpen={setSaveToInventoryDialogOpen}
          startPolling={startPolling}
        />
      )}

      {/* publish to Carmdarket */}
      {publishChangesDialogOpen && (
        <PublishChangesDialog
          articles={
            activeStockType === 'singles'
              ? zustandStock.stock.filter((item) => item.isEdited() && item.idArticle)
              : zustandStock.sealedStock.filter((item) => item.isEdited() && item.idArticle)
          }
          handleChangesPublished={handleChangesPublished}
          open={publishChangesDialogOpen}
          setOpen={setPublishChangesDialogOpen}
          startPolling={startPolling}
        />
      )}
      <PublishErrorsDialog title={t('publish.error')} content={<Box>errors</Box>} />
    </div>
  )
}
